import { Link } from "react-router-dom";
import { Instagram, Sparkles, Heart, GraduationCap } from "lucide-react";

const values = [
  { icon: GraduationCap, title: "Beginner-first", body: "No gatekeeping, no jargon. If it can't be explained simply, we don't know it well enough yet." },
  { icon: Sparkles, title: "Editorial by nature", body: "We treat cocktails like culture, not chemistry. Every recipe has a story, a mood, a moment." },
  { icon: Heart, title: "Responsible always", body: "We celebrate the ritual of drinking — slowly, mindfully, and only when it makes the moment better." },
];

export default function About() {
  return (
    <div className="bg-ink" data-testid="about-page">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-24">
        <p className="eyebrow">About Sip Alchemy</p>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.03] max-w-3xl">
          A love letter to<br /><span className="italic font-serif-editor text-pink-brand">the curious drinker.</span>
        </h1>
        <div className="mt-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 space-y-5 font-body text-lg text-cream/85 leading-relaxed">
            <p>Sip Alchemy started with a simple observation: most Indians drinking today weren't taught how. We learned from Instagram, from bar menus we couldn't decode, from friends who were also making it up as they went.</p>
            <p>So we built the guide we wished we had — half premium drinks magazine, half friendly discovery tool. A place to actually understand whisky, meet cocktails you'll fall in love with, and figure out what to order when the menu feels intimidating.</p>
            <p>We are creator-led, community-fed, and unapologetically opinionated about only two things: <span className="italic">good taste</span> and <span className="italic">responsible drinking</span>.</p>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-xl">
              <img src="https://images.pexels.com/photos/9566294/pexels-photo-9566294.jpeg?auto=compress&cs=tinysrgb&w=900" alt="speakeasy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 text-cream">
                <p className="eyebrow" style={{ color: "#F9C0D2" }}>Made in</p>
                <p className="font-serif-display text-2xl font-bold">Mumbai, India</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink-panel py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow">What we believe</p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-bold text-cream">Three rules, no exceptions.</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {values.map(v => (
              <div key={v.title} className="card-cream p-6" data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <v.icon size={22} className="text-pink-brand" />
                <p className="font-serif-display text-xl font-bold text-cream mt-4">{v.title}</p>
                <p className="text-sm text-cream/70 mt-2 font-body">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="card-ink p-8 sm:p-12 rounded-[2rem] text-center">
          <p className="eyebrow" style={{ color: "#F9C0D2" }}>Say hello</p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-bold text-cream">
            Join us on Instagram <span className="italic font-serif-editor text-pink-brand">@sip_alchemy_</span>
          </h2>
          <p className="mt-3 text-cream/70 font-body max-w-xl mx-auto">Reels, recipes, and the occasional bar-crawl chronicle. Come for the sip, stay for the stories.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://instagram.com/sip_alchemy_" target="_blank" rel="noopener noreferrer" className="btn-pill btn-pink" data-testid="about-instagram">
              <Instagram size={18} /> Follow @sip_alchemy_
            </a>
            <Link to="/contact" className="btn-pill btn-outline-cream" data-testid="about-contact">
              Brand Collabs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
